/**
 * Timeline dei giudizi, un riquadro per giorno di calendario (F-135).
 *
 * ⚠️ **Tre stati, non due.** È l'unica ragione per cui questo componente esiste
 * invece di una riga di badge:
 *
 *  - **giudicato** — il digest di quel giorno ha valutato il titolo;
 *  - **non nominato** — il digest c'era e non ne ha parlato (venduto, uscito
 *    dalla wishlist, non più pertinente);
 *  - **nessun digest** — sabato, domenica, festivo, o una generazione fallita.
 *
 * Appiattirli in «giudicato / non giudicato» farebbe leggere un silenzio del
 * sistema come un silenzio del modello, e viceversa. Sono due cose che portano
 * a due azioni diverse da parte di chi guarda: la prima si spiega, la seconda si
 * indaga.
 *
 * I giorni di calendario ci sono **tutti**, compresi i fine settimana: sono la
 * ragione principale dei buchi, e nasconderli renderebbe la sequenza dei
 * giudizi più fitta di quanto sia davvero.
 */

import { formatDate } from '@/lib/format';
import type { BriefingRating } from './api';
import { CHART_GUTTERS, daysBetween } from './briefing-history';
import './briefing-history.css';

export type TimelineDay = {
  date: string;
  rating: BriefingRating;
  revisionCount: number;
};

type Props = {
  /** Estremi inclusi, `YYYY-MM-DD`. */
  from: string;
  to: string;
  days: readonly TimelineDay[];
  /** I giorni in cui un digest esiste, con o senza questo titolo. */
  briefingDays: readonly string[];
  /** La data evidenziata, condivisa con i grafici sotto. */
  hoveredDate: string | null;
  onHover: (date: string | null) => void;
};

/** Stessa scala e stessi toni del resto dell'applicazione (vedi `RATING_TONE`). */
const RATING_CLASS: Record<BriefingRating, string> = {
  Buy: 'is-buy',
  Overweight: 'is-overweight',
  Hold: 'is-hold',
  Underweight: 'is-underweight',
  Sell: 'is-sell',
};

export function JudgementTimeline({ from, to, days, briefingDays, hoveredDate, onHover }: Props) {
  const byDate = new Map(days.map((day) => [day.date, day]));
  const withBriefing = new Set(briefingDays);

  return (
    <div className="timeline">
      {/* ⚠️ Le stesse gronde dei grafici sotto: un giorno deve stare alla stessa
          ascissa in tutti e tre i riquadri, o il colpo d'occhio verticale — il
          motivo per cui sono impilati — mente. Le gronde arrivano dal grafico e
          non sono ricopiate qui, così non possono divergere. */}
      <ol
        className="timeline__track"
        style={{ paddingLeft: CHART_GUTTERS.left, paddingRight: CHART_GUTTERS.right }}
        onMouseLeave={() => onHover(null)}
      >
        {daysBetween(from, to).map((date) => {
          const judged = byDate.get(date);
          const stato = judged ? 'judged' : withBriefing.has(date) ? 'silent' : 'absent';
          const testo = judged
            ? `${formatDate(date)}: ${judged.rating}${judged.revisionCount > 1 ? ` (rivisto in giornata, ${judged.revisionCount} revisioni)` : ''}`
            : stato === 'silent'
              ? `${formatDate(date)}: il digest non ha valutato questo titolo`
              : `${formatDate(date)}: nessun digest`;

          return (
            <li
              key={date}
              className={`timeline__day timeline__day--${stato} ${judged ? RATING_CLASS[judged.rating] : ''}${
                hoveredDate === date ? ' is-hovered' : ''
              }`}
              title={testo}
              onMouseEnter={() => onHover(date)}
            >
              <span className="visually-hidden">{testo}</span>
              {judged && judged.revisionCount > 1 && <span className="timeline__revised" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>

      <ul className="timeline__legend">
        <li className="timeline__legend-item is-buy">Buy / Overweight</li>
        <li className="timeline__legend-item is-hold">Hold</li>
        <li className="timeline__legend-item is-sell">Underweight / Sell</li>
        <li className="timeline__legend-item timeline__day--silent">Digest senza questo titolo</li>
        <li className="timeline__legend-item timeline__day--absent">Nessun digest</li>
      </ul>
    </div>
  );
}
